'use client';

import { useState } from 'react';
import { Box, Circle, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon } from '@terra/ui-icons';
import { Text } from './text';

export type StepperV2Step = {
  description?: string;
  label: string;
};
type StepperV2Status = 'upcoming' | 'current' | 'completed' | 'disabled';

type StepperV2Orientation = 'vertical' | 'horizontal';

type StepperV2StatusStyle = {
  hoverBackground?: string;
  hoverLabelColor?: string;
  labelColor: string;
};
const STEPPER_V2_STATUS_STYLES: Record<StepperV2Status, StepperV2StatusStyle> =
  {
    upcoming: {
      labelColor: 'fg.neutral.muted',
      hoverBackground: 'bg.neutral.hover',
      hoverLabelColor: 'fg.neutral.default',
    },
    current: {
      labelColor: 'fg.neutral.default',
      hoverBackground: 'bg.neutral.hover',
    },
    completed: {
      labelColor: 'fg.neutral.default',
      hoverBackground: 'bg.neutral.hover',
    },
    disabled: {
      labelColor: 'fg.neutral.subtle',
    },
  };

function getStepperV2Status({
  isCompleted,
  isCurrent,
  isDisabled,
  stepIndex,
}: {
  isCompleted: (stepIndex: number) => boolean;
  isCurrent: (stepIndex: number) => boolean;
  isDisabled: (stepIndex: number) => boolean;
  stepIndex: number;
}): StepperV2Status {
  if (isDisabled(stepIndex)) {
    return 'disabled';
  }

  if (isCompleted(stepIndex)) {
    return 'completed';
  }

  if (isCurrent(stepIndex)) {
    return 'current';
  }

  return 'upcoming';
}

export type StepperV2Variant = 'accent' | 'neutral';

export type StepperV2Props = {
  isCompleted: (stepIndex: number) => boolean;
  isCurrent: (stepIndex: number) => boolean;
  isDisabled: (stepIndex: number) => boolean;
  onStepClick?: (stepIndex: number) => void;
  orientation?: StepperV2Orientation;
  steps: StepperV2Step[];
  variant?: StepperV2Variant;
};
type StepperV2StepRowProps = StepperV2Step & {
  onClick?: () => void;
  status: StepperV2Status;
  stepNumber: number;
  variant: StepperV2Variant;
};

function StepIndicator({
  status,
  stepNumber,
  variant,
}: {
  status: StepperV2Status;
  stepNumber: number;
  variant: StepperV2Variant;
}) {
  const indicatorContent =
    status === 'completed' ? (
      <Tick01Icon size={14} strokeWidth={2.5} />
    ) : (
      stepNumber
    );
  const { circleBackground, circleColor } = getStepIndicatorColors({
    status,
    variant,
  });

  return (
    <Circle
      aria-hidden="true"
      bg={circleBackground}
      color={circleColor}
      flexShrink={0}
      fontSize="xs"
      size="24px"
    >
      {indicatorContent}
    </Circle>
  );
}

function getStepIndicatorColors({
  status,
  variant,
}: {
  status: StepperV2Status;
  variant: StepperV2Variant;
}): { circleBackground: string; circleColor: string } {
  if (variant === 'neutral') {
    if (status === 'current') {
      return {
        circleBackground: 'fg.default',
        circleColor: 'fg.neutral.emphasized',
      };
    }

    return {
      circleBackground: 'bg.neutral.muted',
      circleColor: 'fg.default',
    };
  }

  if (status === 'current' || status === 'completed') {
    return {
      circleBackground: 'terra.9',
      circleColor: 'fg.onaccent.default',
    };
  }

  if (status === 'disabled') {
    return {
      circleBackground: 'terra.3',
      circleColor: 'fg.onaccent.default',
    };
  }

  return {
    circleBackground: 'bg.accent.subtle',
    circleColor: 'fg.onaccent.default',
  };
}

function HorizontalStepIndicator({
  status,
  stepNumber,
}: {
  status: StepperV2Status;
  stepNumber: number;
}) {
  const isActive = status === 'current' || status === 'completed';
  const circleBackground = isActive
    ? 'terra.9'
    : status === 'disabled'
      ? 'terra.3'
      : 'bg.neutral.subtle';

  const indicatorContent =
    status === 'completed' ? (
      <Tick01Icon size={16} strokeWidth={2.5} />
    ) : status === 'current' ? (
      <Box bg="fg.onaccent.default" borderRadius="full" h="8px" w="8px" />
    ) : (
      stepNumber
    );

  return (
    <Circle
      aria-hidden="true"
      bg={circleBackground}
      boxShadow="sm"
      color={isActive ? 'fg.onaccent.default' : 'fg.neutral.muted'}
      flexShrink={0}
      fontSize="sm"
      fontWeight="medium"
      outline={isActive ? '2px solid' : undefined}
      outlineColor={isActive ? 'terra.a3' : undefined}
      outlineOffset={isActive ? '2px' : undefined}
      size="32px"
      zIndex={1}
    >
      {indicatorContent}
    </Circle>
  );
}

function HorizontalStepLabel({
  label,
  status,
}: {
  label: string;
  status: StepperV2Status;
}) {
  const isCurrent = status === 'current';

  return (
    <Box position="relative" pt={2} w="full">
      {isCurrent ? (
        <Box
          borderBottom="6px solid"
          borderBottomColor="terra.9"
          borderLeft="6px solid transparent"
          borderRight="6px solid transparent"
          h={0}
          left="50%"
          position="absolute"
          top={0}
          transform="translateX(-50%)"
          w={0}
        />
      ) : null}
      <Box
        bg={isCurrent ? 'terra.9' : 'bg.neutral.subtle'}
        borderRadius="full"
        boxShadow="sm"
        mx="auto"
        maxW="full"
        px={3}
        py={1}
        w="fit-content"
      >
        <Text
          color={isCurrent ? 'fg.onaccent.default' : 'fg.neutral.muted'}
          fontSize="xs"
          fontWeight="medium"
          lineHeight="1.25"
          maxW="100%"
          overflow="hidden"
          textAlign="center"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
      </Box>
    </Box>
  );
}

function StepperV2Horizontal({
  isCompleted,
  isCurrent,
  isDisabled,
  steps,
}: Pick<StepperV2Props, 'isCompleted' | 'isCurrent' | 'isDisabled' | 'steps'>) {
  return (
    <HStack alignItems="flex-start" gap={0} w="full">
      {steps.map((step, stepIndex) => {
        const status = getStepperV2Status({
          isCompleted,
          isCurrent,
          isDisabled,
          stepIndex,
        });
        const stepNumber = stepIndex + 1;
        const isFirst = stepIndex === 0;
        const isLast = stepIndex === steps.length - 1;

        return (
          <VStack
            key={`${stepIndex}-${step.label}`}
            alignItems="center"
            flex={1}
            gap={4}
            minW={0}
          >
            <HStack alignItems="center" gap={0} w="full">
              <Box
                bg={
                  isFirst
                    ? 'transparent'
                    : isCompleted(stepIndex - 1)
                      ? 'terra.9'
                      : 'border.subtle'
                }
                flex={1}
                h="4px"
                minW={0}
              />
              <HorizontalStepIndicator
                status={status}
                stepNumber={stepNumber}
              />
              <Box
                bg={
                  isLast
                    ? 'transparent'
                    : isCompleted(stepIndex)
                      ? 'terra.9'
                      : 'border.subtle'
                }
                flex={1}
                h="4px"
                minW={0}
              />
            </HStack>
            <HorizontalStepLabel label={step.label} status={status} />
          </VStack>
        );
      })}
    </HStack>
  );
}

function StepperV2StepRow({
  description,
  label,
  onClick,
  status,
  stepNumber,
  variant,
}: StepperV2StepRowProps) {
  const { hoverBackground, hoverLabelColor, labelColor } =
    STEPPER_V2_STATUS_STYLES[status];
  const isInteractive = !!onClick;
  const itemBackground =
    status === 'current'
      ? variant === 'neutral'
        ? 'bg.neutral.subtle'
        : 'terra.a2'
      : 'transparent';
  const descriptionColor =
    status === 'disabled' ? 'fg.neutral.emphasizedMuted' : 'fg.neutral.subtle';
  const [isHovered, setIsHovered] = useState(false);
  const resolvedLabelColor =
    isHovered && isInteractive && hoverLabelColor !== undefined
      ? hoverLabelColor
      : labelColor;
  const content = (
    <HStack
      alignItems="center"
      gap={2}
      px={2}
      py={2.5}
      w="320px"
      borderRadius="sm"
      bg={itemBackground}
      data-status={status}
      cursor={isInteractive ? 'pointer' : 'default'}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      _hover={
        isInteractive && hoverBackground !== undefined
          ? {
              bg: hoverBackground,
            }
          : undefined
      }
    >
      <StepIndicator
        status={status}
        stepNumber={stepNumber}
        variant={variant}
      />
      <Text
        minW={0}
        w="fit-content"
        maxW="100%"
        flexShrink={1}
        fontSize="sm"
        color={resolvedLabelColor}
        lineHeight="1.25"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
      {description !== undefined && description !== '' && (
        <Text
          minW={0}
          w="fit-content"
          maxW="100%"
          flexShrink={1}
          fontSize="xs"
          color={descriptionColor}
          lineHeight="1.25"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {description}
        </Text>
      )}
    </HStack>
  );

  return (
    <Box width="fit-content" onClick={onClick}>
      {content}
    </Box>
  );
}

export function StepperV2({
  isCompleted,
  isCurrent,
  isDisabled,
  onStepClick,
  orientation = 'vertical',
  steps,
  variant = 'accent',
}: StepperV2Props) {
  if (orientation === 'horizontal') {
    return (
      <StepperV2Horizontal
        isCompleted={isCompleted}
        isCurrent={isCurrent}
        isDisabled={isDisabled}
        steps={steps}
      />
    );
  }

  return (
    <VStack alignItems="stretch" gap={2.5}>
      {steps.map((step, stepIndex) => {
        const status = getStepperV2Status({
          isCompleted,
          isCurrent,
          isDisabled,
          stepIndex,
        });
        const stepNumber = stepIndex + 1;
        const onClick =
          status !== 'disabled' && onStepClick
            ? () => onStepClick(stepIndex)
            : undefined;

        return (
          <StepperV2StepRow
            key={stepIndex}
            description={step.description}
            label={step.label}
            onClick={onClick}
            status={status}
            stepNumber={stepNumber}
            variant={variant}
          />
        );
      })}
    </VStack>
  );
}
